"use client";

import { useEffect, useState } from "react";
import AdminColegioLayout from "../components/admincolegiolayout";
import { adminColegioAdministracionService } from "../../../services/admin-colegio/administracion.service";
import type {
  AdminColegioProfesor,
  AdminColegioCurso,
  AdminColegioAsignacion,
} from "../../../app/admin-colegio/administrar/dto/admin.dto";

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

type TipoReporte = "ASIGNACION" | "ASISTENCIA" | "RENDIMIENTO";

export default function AdminColegioReportesPage() {
  const [profesores, setProfesores] = useState<AdminColegioProfesor[]>([]);
  const [cursos, setCursos] = useState<AdminColegioCurso[]>([]);
  const [asignaciones, setAsignaciones] = useState<AdminColegioAsignacion[]>(
    []
  );

  const [alerta, setAlerta] = useState<string | null>(null);
  const [selectedProfesores, setSelectedProfesores] = useState<number[]>([]);
  const [selectedCursos, setSelectedCursos] = useState<number[]>([]);

  const [fechaInicio, setFechaInicio] = useState("");
  const [fechaFin, setFechaFin] = useState("");
  const [tipoReporte, setTipoReporte] = useState<TipoReporte | "">("");
  const [loading, setLoading] = useState(true);

  const [paginaActual, setPaginaActual] = useState(1);
  const resultadosPorPagina = 10;

  useEffect(() => {
    const fetchData = async () => {
      const [profesoresData, cursosData, asignacionesData] = await Promise.all([
        adminColegioAdministracionService.getProfesores(),
        adminColegioAdministracionService.getCursos(),
        adminColegioAdministracionService.getAsignaciones(),
      ]);

      const profesoresDesdeAsignaciones = asignacionesData
        .map((a) => a.profesor)
        .filter((p): p is AdminColegioProfesor => Boolean(p && p.idUsuario));

      const profesoresUnicos = [
        ...profesoresData,
        ...profesoresDesdeAsignaciones,
      ].filter(
        (prof, index, self) =>
          index === self.findIndex((p) => p.idUsuario === prof.idUsuario)
      );

      setProfesores(profesoresUnicos);
      setCursos(cursosData);
      setAsignaciones(asignacionesData);
      setLoading(false);
    };

    fetchData();
  }, []);

  const resultadosFiltrados = asignaciones.filter((a) => {
    const cursoMatch =
      selectedCursos.length > 0 ? selectedCursos.includes(a.cursoId) : true;

    const profesorIdAsignacion = a.profesor?.idUsuario ?? a.profesorId;

    const profesorMatch =
      selectedProfesores.length > 0
        ? selectedProfesores.includes(profesorIdAsignacion)
        : true;

    return cursoMatch && profesorMatch;
  });

  const totalPaginas = Math.ceil(
    resultadosFiltrados.length / resultadosPorPagina
  );

  const resultadosPaginados = resultadosFiltrados.slice(
    (paginaActual - 1) * resultadosPorPagina,
    paginaActual * resultadosPorPagina
  );

  useEffect(() => {
    setPaginaActual(1);
  }, [resultadosFiltrados.length]);

  const limpiarFiltros = () => {
    setTipoReporte("");
    setFechaInicio("");
    setFechaFin("");
    setSelectedProfesores([]);
    setSelectedCursos([]);
  };

  const generarPDF = async () => {
    if (!tipoReporte) {
      alert("Debes seleccionar un tipo de reporte");
      return;
    }

    if (
      (tipoReporte === "ASISTENCIA" || tipoReporte === "RENDIMIENTO") &&
      (!fechaInicio || !fechaFin)
    ) {
      setAlerta(
        "Debes seleccionar fecha de inicio y fecha fin para generar el reporte."
      );
      return;
    }

    const doc = new jsPDF();

    if (tipoReporte === "ASIGNACION") {
      const reporte = await adminColegioAdministracionService.getReporteColegio(
        {
          profesorIds:
            selectedProfesores.length > 0 ? selectedProfesores : undefined,
          cursoIds: selectedCursos.length > 0 ? selectedCursos : undefined,
        }
      );

      let yOffset = 20;
      let primera = true;

      const profesoresSeleccionados = profesores
        .filter((p) => selectedProfesores.includes(p.idUsuario))
        .map((p) => `${p.nombres} ${p.apellidos}`);

      const profesoresConDatos = new Set<string>();

      doc.setFontSize(16);
      doc.text("Reporte de Asignación por Curso", 14, yOffset);
      yOffset += 10;

      for (const profesor of reporte.profesores ?? []) {
        if (
          profesoresSeleccionados.length > 0 &&
          !profesoresSeleccionados.includes(profesor.nombreCompleto)
        ) {
          continue;
        }

        let mostroAlgoProfesor = false;

        for (const curso of profesor.cursos ?? []) {
          if (selectedCursos.length > 0 && !selectedCursos.includes(curso.id)) {
            continue;
          }

          if (!curso.alumnos || curso.alumnos.length === 0) continue;

          profesoresConDatos.add(profesor.nombreCompleto);
          mostroAlgoProfesor = true;

          if (!primera) doc.addPage();
          primera = false;

          let y = yOffset;

          doc.setFontSize(14);
          doc.text(`Profesor: ${profesor.nombreCompleto}`, 14, y);
          y += 8;
          doc.text(`Curso: ${curso.nombre}`, 14, y);
          y += 8;

          autoTable(doc, {
            startY: y,
            head: [["N°", "Alumno"]],
            body: curso.alumnos.map((a: any, i: number) => [
              i + 1,
              a.nombreCompleto,
            ]),
            theme: "grid",
          });
        }

        if (!mostroAlgoProfesor) {
          if (!primera) doc.addPage();
          primera = false;

          doc.setFontSize(14);
          doc.text("Reporte sin resultados", 14, 30);
          doc.setFontSize(12);
          doc.text(`Profesor: ${profesor.nombreCompleto}`, 14, 40);
          doc.text(
            "Este profesor no tiene cursos o alumnos asociados para los filtros seleccionados.",
            14,
            50
          );
        }
      }

      for (const nombreProfesor of profesoresSeleccionados) {
        if (!profesoresConDatos.has(nombreProfesor)) {
          if (!primera) doc.addPage();
          primera = false;

          doc.setFontSize(14);
          doc.text("Reporte sin resultados", 14, 30);
          doc.setFontSize(12);
          doc.text(`Profesor: ${nombreProfesor}`, 14, 40);
          doc.text("Este profesor no tiene asignaciones vigentes.", 14, 50);
        }
      }

      if (selectedProfesores.length === 0 && selectedCursos.length === 0) {
        const profesoresEnReporte = new Set(
          reporte.profesores.map((p: any) => p.nombreCompleto)
        );

        const profesoresDeshabilitados = profesores.filter(
          (p) =>
            !p.vigente &&
            !profesoresEnReporte.has(`${p.nombres} ${p.apellidos}`)
        );

        for (const prof of profesoresDeshabilitados) {
          if (!primera) doc.addPage();
          primera = false;

          doc.setFontSize(14);
          doc.text("Reporte sin resultados", 14, 30);

          doc.setFontSize(12);
          doc.text(`Profesor: ${prof.nombres} ${prof.apellidos}`, 14, 40);
          doc.text(
            "Este profesor no tiene cursos o alumnos asociados para los filtros seleccionados.",
            14,
            50
          );
        }
      }

      doc.save("reporte-asignacion.pdf");
      return;
    }

    const reporte: any =
      tipoReporte === "ASISTENCIA"
        ? await adminColegioAdministracionService.getReporteAsistencia({
            periodoInicio: fechaInicio,
            periodoFin: fechaFin,
            cursoIds: selectedCursos.length > 0 ? selectedCursos : undefined,
          })
        : await adminColegioAdministracionService.getReporteProgreso({
            periodoInicio: fechaInicio,
            periodoFin: fechaFin,
            cursoIds: selectedCursos.length > 0 ? selectedCursos : undefined,
          });

    let primeraPagina = true;

    const profesoresSeleccionados = profesores
      .filter((p) => selectedProfesores.includes(p.idUsuario))
      .map((p) => `${p.nombres} ${p.apellidos}`);

    const profesoresConDatos = new Set<string>();

    for (const curso of reporte.cursos ?? []) {
      if (selectedCursos.length > 0 && !selectedCursos.includes(curso.id)) {
        continue;
      }

      for (const profesor of curso.profesores ?? []) {
        if (
          profesoresSeleccionados.length > 0 &&
          !profesoresSeleccionados.includes(profesor.nombreCompleto)
        ) {
          continue;
        }

        const alumnos = profesor.alumnos ?? [];

        if (alumnos.length === 0) continue;

        profesoresConDatos.add(profesor.nombreCompleto);

        if (!primeraPagina) doc.addPage();
        primeraPagina = false;

        let y = 30;

        doc.setFontSize(16);
        doc.text(
          tipoReporte === "ASISTENCIA"
            ? "Reporte de Asistencia"
            : "Reporte de Rendimiento",
          14,
          20
        );

        doc.setFontSize(12);
        doc.text(`Profesor: ${profesor.nombreCompleto}`, 14, y);
        y += 6;
        doc.text(`Curso: ${curso.nombre}`, 14, y);
        y += 6;
        doc.text(`Período: ${fechaInicio} - ${fechaFin}`, 14, y);
        y += 12;

        autoTable(doc, {
          startY: y,
          head:
            tipoReporte === "ASISTENCIA"
              ? [["N°", "Alumno", "Asistencias", "Inasistencias"]]
              : [
                  [
                    "N°",
                    "Alumno",
                    "Prom. Ensayos",
                    "Prom. Actividades",
                    "% Asistencia",
                  ],
                ],
          body: alumnos.map((a: any, i: number) =>
            tipoReporte === "ASISTENCIA"
              ? [i + 1, a.nombreCompleto, a.asistencias, a.inasistencias]
              : [
                  i + 1,
                  a.nombreCompleto,
                  a.indicadores?.promedioEnsayos ?? "-",
                  a.indicadores?.promedioActividades ?? "-",
                  a.indicadores?.asistenciaPorcentaje != null
                    ? `${a.indicadores.asistenciaPorcentaje}%`
                    : "-",
                ]
          ),
          theme: "grid",
        });
      }
    }

    for (const nombreProfesor of profesoresSeleccionados) {
      if (!profesoresConDatos.has(nombreProfesor)) {
        if (!primeraPagina) doc.addPage();
        primeraPagina = false;

        doc.setFontSize(14);
        doc.text("Reporte sin resultados", 14, 30);

        doc.setFontSize(12);
        doc.text(`Profesor: ${nombreProfesor}`, 14, 40);
        doc.text(
          "Este profesor no tiene cursos o alumnos asociados para los filtros seleccionados.",
          14,
          50
        );
      }
    }

    if (selectedProfesores.length === 0 && selectedCursos.length === 0) {
      const profesoresEnReporte = new Set<string>();

      for (const curso of reporte.cursos ?? []) {
        for (const profesor of curso.profesores ?? []) {
          profesoresEnReporte.add(profesor.nombreCompleto);
        }
      }

      const profesoresDeshabilitados = profesores.filter(
        (p) =>
          !p.vigente && !profesoresEnReporte.has(`${p.nombres} ${p.apellidos}`)
      );

      for (const prof of profesoresDeshabilitados) {
        if (!primeraPagina) doc.addPage();
        primeraPagina = false;

        doc.setFontSize(14);
        doc.text("Reporte sin resultados", 14, 30);

        doc.setFontSize(12);
        doc.text(`Profesor: ${prof.nombres} ${prof.apellidos}`, 14, 40);
        doc.text(
          "Este profesor se encuentra deshabilitado y no tiene información disponible para el período seleccionado.",
          14,
          50
        );
      }
    }

    doc.save(
      tipoReporte === "ASISTENCIA"
        ? "reporte-asistencia.pdf"
        : "reporte-rendimiento.pdf"
    );
  };

  if (loading) {
    return (
      <AdminColegioLayout>
        <div className="flex flex-col items-center justify-center min-h-[60vh]">
          <div className="w-14 h-14 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin mb-6" />

          <p className="text-gray-600 text-lg font-medium">
            Cargando reportes...
          </p>

          <div className="mt-8 w-full max-w-3xl space-y-4">
            <div className="h-6 bg-gray-200 rounded animate-pulse" />
            <div className="h-6 bg-gray-200 rounded animate-pulse w-8/9" />
            <div className="h-6 bg-gray-200 rounded animate-pulse w-8/9" />
          </div>
        </div>
      </AdminColegioLayout>
    );
  }

  return (
    <AdminColegioLayout>
      <h1 className="text-2xl font-bold mb-6">Reportes</h1>

      {alerta && (
        <div className="mb-4 rounded-xl border border-red-300 bg-red-50 p-4 text-red-700 flex justify-between items-center">
          <span>{alerta}</span>
          <button
            onClick={() => setAlerta(null)}
            className="ml-4 font-bold hover:text-red-900"
          >
            ✕
          </button>
        </div>
      )}

      <div className="bg-white p-6 rounded-xl shadow mb-6 grid grid-cols-1 md:grid-cols-5 gap-4">
        <div>
          <label className="block mb-1 font-semibold">Reporte</label>
          <select
            className="border rounded-lg p-2 w-full"
            value={tipoReporte}
            onChange={(e) =>
              setTipoReporte(
                e.target.value === "" ? "" : (e.target.value as TipoReporte)
              )
            }
          >
            <option value="">--Seleccione--</option>
            <option value="ASIGNACION">Asignación por curso</option>
            <option value="ASISTENCIA">Asistencia</option>
            <option value="RENDIMIENTO">Rendimiento</option>
          </select>
        </div>

        {tipoReporte !== "" && (
          <>
            <div>
              <label className="block mb-1 font-semibold">Cursos</label>
              <div className="border rounded-lg p-2 w-full max-h-40 overflow-y-auto">
                {cursos.map((c) => (
                  <label
                    key={c.idCurso}
                    className="flex items-center gap-2 mb-1"
                  >
                    <input
                      type="checkbox"
                      checked={selectedCursos.includes(c.idCurso)}
                      onChange={(e) =>
                        setSelectedCursos((prev) =>
                          e.target.checked
                            ? [...prev, c.idCurso]
                            : prev.filter((id) => id !== c.idCurso)
                        )
                      }
                    />
                    {c.nombre}
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block mb-1 font-semibold">Profesores</label>
              <div className="border rounded-lg p-2 w-full max-h-40 overflow-y-auto">
                {profesores.map((p) => (
                  <label
                    key={p.idUsuario}
                    className="flex items-center gap-2 mb-1"
                  >
                    <input
                      type="checkbox"
                      checked={selectedProfesores.includes(p.idUsuario)}
                      onChange={(e) =>
                        setSelectedProfesores((prev) =>
                          e.target.checked
                            ? [...prev, p.idUsuario]
                            : prev.filter((id) => id !== p.idUsuario)
                        )
                      }
                    />
                    {p.nombres} {p.apellidos}
                  </label>
                ))}
              </div>
            </div>

            {(tipoReporte === "ASISTENCIA" ||
              tipoReporte === "RENDIMIENTO") && (
              <>
                <div>
                  <label className="block mb-1 font-semibold">
                    Fecha inicio
                  </label>
                  <input
                    type="date"
                    className="border rounded-lg p-2 w-full"
                    value={fechaInicio}
                    onChange={(e) => setFechaInicio(e.target.value)}
                  />
                </div>

                <div>
                  <label className="block mb-1 font-semibold">Fecha fin</label>
                  <input
                    type="date"
                    className="border rounded-lg p-2 w-full"
                    value={fechaFin}
                    onChange={(e) => setFechaFin(e.target.value)}
                  />
                </div>
              </>
            )}
          </>
        )}
      </div>

      <div className="flex justify-end gap-3 mb-4">
        <button
          onClick={limpiarFiltros}
          className="px-4 py-2 bg-gray-300 text-gray-800 rounded-lg shadow hover:bg-gray-400"
        >
          Limpiar filtros
        </button>

        <button
          onClick={generarPDF}
          className="px-4 py-2 bg-purple-600 text-white rounded-lg shadow hover:bg-purple-700"
        >
          Descargar PDF
        </button>
      </div>

      {resultadosFiltrados.length > 0 && (
        <div className="bg-white p-6 rounded-xl shadow overflow-x-auto">
          <table className="w-full border table-auto">
            <thead className="bg-purple-600 text-white">
              <tr>
                <th className="p-3 text-left">Curso</th>
                <th className="p-3 text-left">Profesor</th>
                <th className="p-3 text-left">Vigente</th>
              </tr>
            </thead>
            <tbody>
              {resultadosPaginados.map((a) => {
                const curso = cursos.find((c) => c.idCurso === a.cursoId);

                const profesor =
                  a.profesor ??
                  profesores.find((p) => p.idUsuario === a.profesorId);

                return (
                  <tr
                    key={a.idAsignacion}
                    className="border-t hover:bg-gray-50"
                  >
                    <td className="p-3">{curso?.nombre || "-"}</td>
                    <td className="p-3">
                      {profesor
                        ? `${profesor.nombres} ${profesor.apellidos}`
                        : "-"}
                    </td>
                    <td className="p-3">{a.vigente ? "Sí" : "No"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {totalPaginas > 1 && (
            <div className="flex justify-between items-center mt-4">
              <span className="text-sm text-gray-600">
                Página {paginaActual} de {totalPaginas}
              </span>

              <div className="flex gap-2">
                <button
                  disabled={paginaActual === 1}
                  onClick={() => setPaginaActual((p) => p - 1)}
                  className="px-3 py-1 rounded-lg border bg-white disabled:opacity-50"
                >
                  Anterior
                </button>

                <button
                  disabled={paginaActual === totalPaginas}
                  onClick={() => setPaginaActual((p) => p + 1)}
                  className="px-3 py-1 rounded-lg border bg-white disabled:opacity-50"
                >
                  Siguiente
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </AdminColegioLayout>
  );
}
